/* Keep Simple Bundles' selects as the source of option, stock and cart data. */
(() => {
  'use strict';
  const rootSelector = '[id="simple-bundles-io-options"]';
  const controls = new Map();
  let nextId = 0;
  let queued = false;
  let active = false;

  function eligible(select) {
    const options = [...select.options].filter((option) => option.value !== '');
    return !select.multiple && options.length > 0 &&
      options.every((option) => /^(XS|S|M|L)$/.test(option.value)) &&
      new Set(options.map((option) => option.value)).size === options.length;
  }

  function destroy(select, control) {
    control.group.remove();
    control.label?.classList.remove('mk-bundle-sizes__source');
    select.classList.remove('mk-bundle-sizes__source');
    for (const [name, value] of Object.entries(control.attributes)) {
      if (value === null) select.removeAttribute(name);
      else select.setAttribute(name, value);
    }
    select.removeEventListener('invalid', control.invalid);
    controls.delete(select);
  }

  function create(select) {
    const label = [...(select.labels || [])].find((item) => item.closest(rootSelector));
    if (!label) return null;
    const group = document.createElement('fieldset');
    group.className = 'mk-bundle-sizes';
    const legend = document.createElement('legend');
    legend.className = 'mk-bundle-sizes__title';
    group.append(legend);
    const choices = document.createElement('div');
    choices.className = 'mk-bundle-sizes__choices';
    group.append(choices);
    const id = `mk-bundle-sizes-${++nextId}`;
    const attributes = {
      'aria-hidden': select.getAttribute('aria-hidden'),
      tabindex: select.getAttribute('tabindex'),
    };
    const invalid = (event) => {
      event.preventDefault();
      group.setAttribute('aria-invalid', 'true');
      choices.querySelector('input:not(:disabled)')?.focus();
    };
    const control = { group, legend, choices, label, id, attributes, invalid, signature: '' };
    group.addEventListener('change', (event) => {
      const radio = event.target;
      if (!(radio instanceof HTMLInputElement) || !radio.checked || radio.disabled) return;
      select.value = radio.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync(select, control);
    });
    select.addEventListener('invalid', invalid);
    select.after(group);
    select.classList.add('mk-bundle-sizes__source');
    label.classList.add('mk-bundle-sizes__source');
    select.setAttribute('aria-hidden', 'true');
    select.setAttribute('tabindex', '-1');
    controls.set(select, control);
    return control;
  }

  function sync(select, control) {
    if (!select.isConnected) return;
    const title = control.label.textContent.trim();
    if (control.legend.textContent !== title) control.legend.textContent = title;
    const options = [...select.options].filter((option) => option.value !== '');
    const signature = JSON.stringify(options.map((option) => [option.value, option.textContent.trim()]));
    if (signature !== control.signature) {
      control.choices.replaceChildren();
      for (const option of options) {
        const label = document.createElement('label');
        label.className = 'mk-bundle-sizes__choice';
        const radio = document.createElement('input');
        radio.className = 'mk-bundle-sizes__input';
        radio.type = 'radio';
        radio.name = control.id;
        // A non-existent explicit owner keeps proxy fields out of Shopify FormData.
        radio.setAttribute('form', `${control.id}-presentation-only`);
        radio.value = option.value;
        const text = document.createElement('span');
        text.className = 'mk-bundle-sizes__button';
        text.textContent = option.textContent.trim();
        label.append(radio, text);
        control.choices.append(label);
      }
      control.signature = signature;
    }
    const radios = [...control.choices.querySelectorAll('input')];
    for (let index = 0; index < options.length; index++) {
      radios[index].checked = select.value === options[index].value;
      radios[index].disabled = select.disabled || options[index].disabled;
    }
    if (select.validity.valid) control.group.removeAttribute('aria-invalid');
  }

  function reconcile() {
    queued = false;
    if (!active) return;
    for (const [select, control] of controls) {
      if (!select.isConnected || !select.closest(rootSelector) || !eligible(select) ||
          !control.group.isConnected || !control.label.isConnected) destroy(select, control);
    }
    for (const select of document.querySelectorAll(`${rootSelector} select`)) {
      if (!eligible(select)) continue;
      const control = controls.get(select) || create(select);
      if (control) sync(select, control);
    }
  }

  function schedule() {
    if (queued || !active) return;
    queued = true;
    queueMicrotask(reconcile);
  }

  const observer = new MutationObserver((records) => {
    if (records.some((record) => {
      if (record.target instanceof Element && record.target.closest('.mk-bundle-sizes')) return false;
      if (record.type === 'attributes') return record.target.closest(rootSelector);
      if (record.target instanceof Element && record.target.closest(rootSelector)) return true;
      return [...record.addedNodes, ...record.removedNodes].some((node) =>
        node instanceof Element && (node.matches(rootSelector) || node.querySelector(rootSelector)));
    })) schedule();
  });

  function resume() {
    active = true;
    observer.observe(document.body, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['disabled', 'selected', 'value', 'required'],
    });
    reconcile();
  }

  function start() {
    document.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement && event.target.closest(rootSelector)) schedule();
    });
    document.addEventListener('reset', () => setTimeout(schedule, 0));
    window.addEventListener('pagehide', () => {
      active = false;
      observer.disconnect();
    });
    window.addEventListener('pageshow', resume);
    resume();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
